import { useEffect } from 'react';

/**
 * Dynamic scaling hook that adjusts the viewport based on a reference display size
 * This ensures the website looks identical across all display sizes
 */
export const useDynamicScale = () => {
  useEffect(() => {
    const REFERENCE_WIDTH = 1512; // Your Mac display width where it looks perfect
    const REFERENCE_HEIGHT = 828; // Your Mac display height
    
    const updateScale = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      
      // Calculate scale factors for both dimensions
      const widthScale = currentWidth / REFERENCE_WIDTH;
      const heightScale = currentHeight / REFERENCE_HEIGHT;
      
      // Use the smaller scale to ensure content fits
      // This maintains aspect ratio and prevents overflow
      const scale = Math.min(widthScale, heightScale);
      
      // Apply the scale as a CSS custom property
      document.documentElement.style.setProperty('--dynamic-scale', scale.toString());
      
      // Also calculate individual axis scales for fine-tuned control
      document.documentElement.style.setProperty('--width-scale', widthScale.toString());
      document.documentElement.style.setProperty('--height-scale', heightScale.toString());
      
      // Set a comfortable font size scale (less aggressive than full scale)
      // This prevents text from becoming too large on big displays
      const fontScale = Math.min(1 + (scale - 1) * 0.5, 1.15);
      document.documentElement.style.setProperty('--font-scale', fontScale.toString());
      
      // Calculate vertical spacing scale to fit content in shorter displays
      // On displays shorter than reference (like 1512x792 vs 1512x828), reduce spacing
      const verticalScale = Math.min(heightScale, 1.0);
      document.documentElement.style.setProperty('--vertical-scale', verticalScale.toString());
    };
    
    // Update on mount
    updateScale();
    
    // Update on resize with debouncing
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateScale, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);
};
